import { attach, combine, createEvent, createStore } from 'effector';
import { $contentBlock } from '@nx-frontend/shared/store/portal-blocks';
import { $virtualPortalNodes } from '@nx-frontend/shared/store/virtual-portal-node';
import { PortalPage } from '@nx-frontend/shared/types';

export const initialPageState: PortalPage = {
  id: '',
  title: '',
  path: '',
  visible: false,
};

export type UpdatePagePayload = Omit<PortalPage, 'visible'>;
export type ChangePageVisibilityPayload = Pick<PortalPage, 'id' | 'visible'>;
export const setPageEvent = createEvent<PortalPage>();
export const updatePageEvent = createEvent<UpdatePagePayload>();
export const updatePagePathBySlugEvent = createEvent<{ id: string; slug: string }>();
export const updatePagePermissionsEvent = createEvent<Pick<PortalPage, 'id' | 'permissions'>>();
export const changePageVisibilityEvent = createEvent<ChangePageVisibilityPayload>();
export const updatePageTitleByPathEvent = createEvent<{ title: string; path: string }>();

export const $page = createStore(initialPageState)
  .on(setPageEvent, (state, page: PortalPage | null) => {
    if (page) {
      return { ...page };
    }
    return state;
  })
  .on(updatePageTitleByPathEvent, (state, { title, path }) => {
    if (path === state.path) {
      return {
        ...state,
        title,
      };
    }
    return state;
  })
  .on(updatePageEvent, (state, page) => {
    return {
      ...state,
      ...page,
    };
  })
  .on(changePageVisibilityEvent, (state, { visible, id }) => {
    if (id === state.id) {
      return {
        ...state,
        visible,
      };
    }
    return state;
  })
  .on(updatePagePathBySlugEvent, (state, { id, slug }) => {
    if (id === state.id) {
      const pathParts = state.path.split('/');
      if (pathParts.length > 0) {
        pathParts.pop();
      }
      pathParts.push(slug);
      const updatedPath = pathParts.join('/');
      return {
        ...state,
        path: updatedPath,
      };
    }

    return state;
  })
  .on(updatePagePermissionsEvent, (state, { id, permissions }) => {
    return {
      ...state,
      permissions: id === state.id ? permissions : state.permissions,
    };
  });

export const setPageFx = attach({
  source: [$virtualPortalNodes, $page],
  effect: ([portalNodeState, page], { path }) => {
    if (page.path === path) {
      const nodes = Object.values(portalNodeState.entities);
      const currentNode = nodes.find((node) => node.path === path);
      if (currentNode) {
        setPageEvent(currentNode);
      }
    }
    return null;
  },
});

export interface PageInfo {
  path: string;
  title?: string;
  pageId: string;
  visible: boolean;
}

export const $pageInfo = $page.map(
  ({ id: pageId, path, title, visible }): PageInfo => ({
    path,
    title,
    pageId,
    visible,
  })
);

export const $pageTitle = combine(
  $page,
  $contentBlock,
  (page, contentBlock) => page.title ?? contentBlock?.title ?? ''
);

export const $isPageProjection = $page.map((page) => !!(page.isOrigin === false && page.originGlobalId));

export const $pagePermissions = $page.map((page) => page.permissions);
